import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetModelTabs, { ASSET_MODEL_TABS } from "../components/AssetModelTabs";
import { useAuth } from "../context/AuthContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  Brain, UsersThree, AddressBook, MagnifyingGlass, CircleNotch, Buildings, User,
  Briefcase, ClipboardText, FileText, MapPin, ArrowRight, Info, WarningCircle, Lightning, ArrowClockwise, Cube,
} from "@phosphor-icons/react";

const BLUE = "#0055FF";
const STATUS_COLORS = {
  NOT_STARTED: "#6B7280", IN_PROGRESS: "#0055FF", COMPLETED: "#059669",
  ARCHIVED: "#9CA3AF", INVOICED: "#7C3AED", CANCELLED: "#DC2626", PENDING: "#0055FF",
  ACCEPTED: "#059669", REJECTED: "#DC2626", UNKNOWN: "#9CA3AF",
};
const sc = (s) => STATUS_COLORS[(s || "").toUpperCase()] || "#6B7280";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid={`live-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={16} weight="bold" /><span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`mt-2 font-heading text-3xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

function LiveActivity({ live, navigate, isAdmin }) {
  const totals = useMemo(() => {
    const pc = live?.per_client || [];
    return {
      total_jobs: pc.reduce((s, p) => s + (p.total_jobs || 0), 0),
      open_jobs: pc.reduce((s, p) => s + (p.open_jobs || 0), 0),
      total_quotes: pc.reduce((s, p) => s + (p.total_quotes || 0), 0),
      assets: pc.reduce((s, p) => s + (p.assets || 0), 0),
    };
  }, [live]);
  if (!live || live.empty) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 border border-dashed border-neutral-300 rounded-sm text-neutral-500">
        <WarningCircle size={26} className="text-[#D97706]" /> No live jobs or quotes found for this portfolio yet.
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="live-activity">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        {live.kind === "group" && <Kpi icon={UsersThree} label="Clients" value={live.kpis.clients} />}
        <Kpi icon={ClipboardText} label="Total Jobs" value={live.kpis.total_jobs.toLocaleString()} accent="text-[#0055FF]" />
        <Kpi icon={Briefcase} label="Open Jobs" value={live.kpis.open_jobs} accent="text-[#D97706]" />
        <Kpi icon={FileText} label="Quotes" value={live.kpis.total_quotes.toLocaleString()} />
        <Kpi icon={MapPin} label="Sites" value={live.kpis.sites} />
        <Kpi icon={Cube} label="Assets" value={(live.kpis.assets ?? 0).toLocaleString()} accent="text-[#059669]" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
          <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Job Status <span className="text-xs font-normal text-neutral-400">(recent sample)</span></h3>
          {live.job_status.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={live.job_status} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {live.job_status.map((s) => <Cell key={s.label} fill={sc(s.label)} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="py-8 text-center text-sm text-neutral-400">No job data.</p>}
        </div>
        <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
          <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Work Types <span className="text-xs font-normal text-neutral-400">(recent sample)</span></h3>
          {live.job_types.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={live.job_types} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 12 }} /><YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill={BLUE} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="py-8 text-center text-sm text-neutral-400">No type data.</p>}
        </div>
      </div>

      {live.kind === "group" && (
        <div className="border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="per-client-table">
          <div className="border-b border-neutral-200 px-5 py-3"><h3 className="font-heading text-lg font-black tracking-tight">By Client</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-5 py-2.5 text-left">Client</th><th className="px-5 py-2.5 text-right">Total Jobs</th><th className="px-5 py-2.5 text-right">Open</th><th className="px-5 py-2.5 text-right">Quotes</th><th className="px-5 py-2.5 text-right">Assets</th><th className="px-5 py-2.5"></th>
              </tr></thead>
              <tbody>
                {live.per_client.map((p) => (
                  <tr key={p.id} className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03]" onClick={() => navigate(`/clients/${p.id}`)} data-testid={`per-client-${p.id}`} title="Open client detail & jobs">
                    <td className="px-5 py-2.5 font-semibold text-[#0055FF]">{p.name}</td>
                    <td className="px-5 py-2.5 text-right font-mono">{p.total_jobs.toLocaleString()}</td>
                    <td className="px-5 py-2.5 text-right font-mono">{p.open_jobs}</td>
                    <td className="px-5 py-2.5 text-right font-mono">{p.total_quotes}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-[#059669]">{(p.assets ?? 0).toLocaleString()}</td>
                    <td className="px-5 py-2.5 text-right"><ArrowRight size={15} className="ml-auto text-neutral-300" /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold text-[#0A0A0A]" data-testid="per-client-totals">
                  <td className="px-5 py-2.5 text-left uppercase text-xs tracking-wider text-neutral-500">Totals</td>
                  <td className="px-5 py-2.5 text-right font-mono">{totals.total_jobs.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right font-mono">{totals.open_jobs.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right font-mono">{totals.total_quotes.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-[#059669]">{totals.assets.toLocaleString()}</td>
                  <td className="px-5 py-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="recent-jobs-table">
        <div className="border-b border-neutral-200 px-5 py-3"><h3 className="font-heading text-lg font-black tracking-tight">Recent Jobs</h3></div>
        {live.recent_jobs.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-neutral-500">No jobs found.</div>
        ) : (
          <div className="max-h-[440px] overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-neutral-50"><tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-2.5 text-left">Job #</th><th className="px-4 py-2.5 text-left">Description</th><th className="px-4 py-2.5 text-left">Type</th><th className="px-4 py-2.5 text-left">Site</th><th className="px-4 py-2.5 text-left">Status</th>
              </tr></thead>
              <tbody>
                {live.recent_jobs.map((t) => (
                  <tr key={t.id} className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03]" onClick={() => navigate(`/jobs/${t.id}`)} data-testid={`live-job-${t.jobNumber}`}>
                    <td className="px-4 py-2.5 font-mono font-semibold text-[#0055FF]">{t.jobNumber}</td>
                    <td className="max-w-[260px] truncate px-4 py-2.5" title={t.name}>{t.name || "—"}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{t.taskType || "—"}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{t.site || "—"}</td>
                    <td className="px-4 py-2.5"><span className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: sc(t.status) + "1a", color: sc(t.status) }}>{t.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="asset-list-table">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="font-heading text-lg font-black tracking-tight">Asset List</h3>
          <span className="text-xs text-neutral-400">{(live.kpis.assets ?? 0).toLocaleString()} assets managed{live.assets_capped ? ` · showing first ${(live.assets || []).length}` : ""}</span>
        </div>
        {!live.assets || live.assets.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-neutral-500">No assets found for this {live.kind === "group" ? "group" : "client"}.</div>
        ) : (
          <div className="max-h-[440px] overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-neutral-50"><tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-2.5 text-left">Asset</th><th className="px-4 py-2.5 text-left">Type</th>
                {live.kind === "group" && <th className="px-4 py-2.5 text-left">Client</th>}
                <th className="px-4 py-2.5 text-left">Manufacturer / Model</th><th className="px-4 py-2.5 text-left">Serial</th>
              </tr></thead>
              <tbody>
                {live.assets.map((a) => {
                  const clickable = isAdmin && a.asset_id;
                  return (
                    <tr key={a.asset_id || `${a.asset_name}-${a.serial_number}`}
                      className={`border-b border-neutral-100 even:bg-neutral-50/60 ${clickable ? "cursor-pointer hover:bg-[#0055FF]/[0.03]" : ""}`}
                      onClick={clickable ? () => navigate(`/asset-detail?id=${encodeURIComponent(a.asset_id)}`) : undefined}
                      data-testid={`live-asset-${a.asset_id || ""}`}>
                      <td className={`max-w-[260px] truncate px-4 py-2.5 font-semibold ${clickable ? "text-[#0055FF]" : "text-[#0A0A0A]"}`} title={a.asset_name}>{a.asset_name || "—"}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{a.category_name || "—"}</td>
                      {live.kind === "group" && <td className="max-w-[180px] truncate px-4 py-2.5 text-neutral-600" title={a.client_name}>{a.client_name || "—"}</td>}
                      <td className="px-4 py-2.5 text-neutral-600">{[a.manufacturer, a.model_number].filter(Boolean).join(" / ") || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-neutral-600">{a.serial_number || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {live.note && (
        <div className="flex items-start gap-2 text-xs text-neutral-400" data-testid="live-note">
          <Info size={14} className="mt-0.5 shrink-0" /> {live.note}
        </div>
      )}
    </div>
  );
}

export default function AssetIntelligenceLive() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const lockedKind = searchParams.get("kind");
  const lockedRef = searchParams.get("ref");
  const isLocked = !!(lockedKind && lockedRef);
  const isAdmin = user?.role === "admin";

  // selector state (admin only)
  const [mode, setMode] = useState("group");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const debounce = useRef(null);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("live");

  const TABS = [{ id: "live", label: "Live Activity" }, ...ASSET_MODEL_TABS];

  useEffect(() => {
    if (isAdmin) api.get("/client-groups").then((r) => setGroups(r.data.groups)).catch(() => {});
  }, [isAdmin]);

  const loadReport = useCallback(async (kind, ref) => {
    if (!ref) return;
    setLoading(true); setError(""); setReport(null);
    try {
      const { data } = await api.get("/intelligence/report", { params: { kind, ref } });
      setReport(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  // Auto-load when a scope is locked via the URL (client deep-link).
  useEffect(() => {
    if (isLocked) loadReport(lockedKind, lockedRef);
  }, [isLocked, lockedKind, lockedRef, loadReport]);

  const onQuery = (v) => {
    setQuery(v); setSelectedClient(null);
    clearTimeout(debounce.current);
    if (v.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { search: v, limit: 8, page: 1 } });
        setResults(data.items);
      } finally { setSearching(false); }
    }, 300);
  };

  const generate = () => {
    const kind = mode;
    const ref = mode === "group" ? selectedGroup : selectedClient?.id;
    if (ref) loadReport(kind, ref);
  };

  const refresh = async () => {
    if (!report) return;
    setPulling(true); setError("");
    try {
      const { data } = await api.post("/intelligence/report/refresh", null, { params: { kind: report.kind, ref: report.ref } });
      setReport(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPulling(false); }
  };

  const canGenerate = mode === "group" ? !!selectedGroup : !!selectedClient;
  const ps = report?.pull_status;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <Layout>
      <div data-testid="asset-intelligence-live-page">
        {!isLocked && (
          <button onClick={() => navigate(isAdmin ? "/dashboard" : "/home")} className="mb-2 text-sm font-semibold text-neutral-400 hover:text-[#0055FF]">← Back</button>
        )}
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Asset Intelligence</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
          {isLocked && report ? report.title : "Asset Intelligence"}
        </h1>
        {!isLocked && (
          <p className="mt-3 max-w-2xl text-base text-neutral-600">
            Pick a client or a client group to open its saved intelligence report — executive dashboard, capital plan,
            priority assets and live Aroflo activity. Reports are stored and only refreshed when you click Update.
          </p>
        )}

        {/* Selector — admins only, when not locked to a scope */}
        {isAdmin && !isLocked && (
          <div className="mt-6 border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <div className="mb-4 inline-flex rounded-sm border border-neutral-200 p-1" data-testid="mode-toggle">
              <button data-testid="mode-group" onClick={() => { setMode("group"); setReport(null); }} className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${mode === "group" ? "bg-[#0A0A0A] text-white" : "text-neutral-600"}`}>
                <UsersThree size={16} weight="bold" /> Client Group
              </button>
              <button data-testid="mode-client" onClick={() => { setMode("client"); setReport(null); }} className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${mode === "client" ? "bg-[#0A0A0A] text-white" : "text-neutral-600"}`}>
                <AddressBook size={16} weight="bold" /> Single Client
              </button>
            </div>
            {mode === "group" ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Select a group</label>
                  <select data-testid="group-select" value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none focus:border-[#0055FF]">
                    <option value="">— Choose a client group —</option>
                    {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name} ({g.client_count})</option>)}
                  </select>
                  {groups.length === 0 && <p className="mt-1.5 text-sm text-neutral-500">No groups yet. <button onClick={() => navigate("/client-groups")} className="font-semibold text-[#0055FF] hover:underline">Create one</button>.</p>}
                </div>
                <button data-testid="generate-button" disabled={!canGenerate || loading} onClick={generate} className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50">
                  {loading ? <CircleNotch size={18} className="animate-spin" /> : <Brain size={18} weight="bold" />} Generate report
                </button>
              </div>
            ) : (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Find a client</label>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="relative flex-1 min-w-[260px]">
                    <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input data-testid="client-search" value={selectedClient ? selectedClient.name : query} onChange={(e) => onQuery(e.target.value)} placeholder="Start typing a client name…" className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none focus:border-[#0055FF]" />
                    {!selectedClient && results && (
                      <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-sm border border-neutral-200 bg-white shadow-lg" data-testid="client-results">
                        {searching && <li className="px-3 py-2 text-sm text-neutral-500">Searching…</li>}
                        {!searching && results.length === 0 && <li className="px-3 py-2 text-sm text-neutral-500">No matches.</li>}
                        {results.map((c) => (
                          <li key={c.id}>
                            <button data-testid={`pick-client-${c.id}`} onClick={() => { setSelectedClient(c); setResults(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#0055FF]/5">
                              {c.individual ? <User size={15} className="text-neutral-400" /> : <Buildings size={15} className="text-neutral-400" />}
                              <span className="truncate">{c.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button data-testid="generate-button" disabled={!canGenerate || loading} onClick={generate} className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50">
                    {loading ? <CircleNotch size={18} className="animate-spin" /> : <Brain size={18} weight="bold" />} Generate report
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <div className="mt-4 rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-4 py-3 text-sm text-[#DC2626]" data-testid="live-error">{error}</div>}

        {loading && (
          <div className="mt-10 flex flex-col items-center justify-center gap-3 text-neutral-500" data-testid="live-loading">
            <CircleNotch size={30} className="animate-spin text-[#0055FF]" />
            <p className="text-sm">Loading intelligence report…</p>
          </div>
        )}

        {report && !loading && (
          <div className="mt-6 space-y-6" data-testid="live-report">
            {/* Header: title + freshness + UPDATE */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white">
                  {report.kind === "group" ? <UsersThree size={24} weight="fill" /> : <Buildings size={24} weight="fill" />}
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-[#0A0A0A]" data-testid="report-title">{report.title}</h2>
                  <p className="text-sm text-neutral-500" data-testid="report-last-updated">
                    Last updated <span className="font-semibold text-neutral-700">{fmtTime(report.generated_at)}</span>
                    {report.as_of && <span className="text-neutral-400"> · data as of {fmtTime(report.as_of)}</span>}
                  </p>
                </div>
              </div>
              <button
                data-testid="update-report-button"
                onClick={refresh}
                disabled={pulling || (ps && !ps.can_pull)}
                title={ps && !ps.can_pull ? `Available again in ${Math.ceil(ps.seconds_remaining / 60)} min` : "Fetch the latest data from Aroflo"}
                className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50"
              >
                {pulling ? <CircleNotch size={16} className="animate-spin" /> : <ArrowClockwise size={16} weight="bold" />}
                {ps && !ps.can_pull ? `Update in ${Math.ceil(ps.seconds_remaining / 60)}m` : "UPDATE"}
              </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {TABS.map((t) => (
                <button key={t.id} data-testid={`live-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 rounded-sm px-3.5 py-2 text-sm font-semibold transition-all duration-200 ${tab === t.id ? "bg-[#0A0A0A] text-white" : "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}>
                  {t.id === "live" && <Lightning size={15} weight="fill" className="text-[#D97706]" />} {t.label}
                </button>
              ))}
            </div>

            {tab === "live"
              ? <div className="mt-1"><LiveActivity live={report.live} navigate={navigate} /></div>
              : <AssetModelTabs tab={tab} data={report.sample} />}
          </div>
        )}
      </div>
    </Layout>
  );
}
